import { ScaleElement } from '@scale/base-element';
import { VariantSelectedEvent } from '@scale/events';

class VariantPicker extends ScaleElement {
  #variants = [];

  setup() {
    const json = this.querySelector('[data-ref="variants-json"]');
    if (json) this.#variants = JSON.parse(json.textContent);

    this.#setupMediaPreview();
  }

  handleOptionChange() {
    const selectedOptions = this.#getSelectedOptions();
    const variant = this.#findVariant(selectedOptions);

    if (!variant) return;

    this.#updateURL(variant);
    this.#updateFormInput(variant);
    this.#updateSelectedLabels(selectedOptions);
    this.#updateAvailability(selectedOptions);
    this.#handleCombinedListing(selectedOptions);

    this.broadcast(new VariantSelectedEvent(variant));
  }

  #getSelectedOptions() {
    const options = [];
    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const checked = fieldset.querySelector('input:checked') || fieldset.querySelector('select');
      if (checked) options.push(checked.value);
    }
    return options;
  }

  #findVariant(selectedOptions) {
    return this.#variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  #updateURL(variant) {
    if (this.hasAttribute('data-skip-url-update')) return;
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  #updateFormInput(variant) {
    const form = this.closest('form') || document.querySelector(`form[data-type="add-to-cart-form"]`);
    const input = form?.querySelector('input[name="id"]');
    if (input) {
      input.value = variant.id;
      input.disabled = !variant.available;
    }
  }

  #updateSelectedLabels(selectedOptions) {
    selectedOptions.forEach((value, i) => {
      const label = this.ref(`selected-value-${i}`);
      if (label) label.textContent = value;
    });
  }

  #updateAvailability(selectedOptions) {
    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const index = Number(fieldset.dataset.optionIndex);
      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        const testOptions = [...selectedOptions];
        testOptions[index] = input.value;
        const match = this.#variants.some((v) =>
          v.available && v.options.every((opt, i) => opt === testOptions[i])
        );
        const label = input.closest('label');
        if (label) {
          label.classList.toggle('product-variant-picker__button--unavailable', !match);
          label.classList.toggle('product-variant-picker__swatch--unavailable', !match);
        }
      }
    }
  }

  // ── Media preview on hover ──

  #setupMediaPreview() {
    for (const label of this.querySelectorAll('.product-variant-picker__button, .product-variant-picker__swatch')) {
      const input = label.querySelector('input[type="radio"]');
      if (!input) continue;

      this.on('mouseenter', () => {
        this.debounce('media-preview', () => {
          const variant = this.#findVariantForPreview(input);
          if (variant?.featured_media?.id) {
            this.emit('scale:media:preview', { mediaId: variant.featured_media.id });
          }
        }, 80);
      }, { target: label });
    }
  }

  #findVariantForPreview(input) {
    const fieldset = input.closest('fieldset');
    const index = Number(fieldset.dataset.optionIndex);
    const selectedOptions = [...this.#getSelectedOptions()];
    selectedOptions[index] = input.value;
    return this.#findVariant(selectedOptions);
  }

  #handleCombinedListing(selectedOptions) {
    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const index = Number(fieldset.dataset.optionIndex);
      const value = selectedOptions[index];
      const link = fieldset.querySelector(`[data-combined-listing="${CSS.escape(value)}"]`);
      if (link) window.location.href = link.href;
    }
  }
}

if (!customElements.get('product-variant-picker')) {
  customElements.define('product-variant-picker', VariantPicker);
}
